import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, of, switchMap } from 'rxjs';
import { environment } from '@environments/environment';
import { GoodsSkuControllerService } from '@app/api/api/goodsSkuController.service';
import { SkuAuditControllerService } from '@app/api/api/skuAuditController.service';
import { PageInfoGoodsSkuPageVO } from '@app/api/model/pageInfoGoodsSkuPageVO';
import { GoodsSkuPageVO } from '@app/api/goods-service/model/goodsSkuPageVO';
import { VendorSkuVO } from '@app/api/goods-service/model/vendorSkuVO';
import { SkuUpdateDTO } from '@app/api/model/skuUpdateDTO';
import { SkuAttributeValue } from '@app/api/model/skuAttributeValue';
import { BarcodeRequestDTO } from '@app/api/goods-service/model/barcodeRequestDTO';
import { BASE_PATH } from '@app/api';
import { SyncLogItem } from '../../sync-report/sync-report.config';
import {
  SKUItem,
  SKUDetail,
  SKUFormData,
  SKUType,
  SKUApiItem,
  SKUSearchPayload,
  SKUPaginatedResponse,
  SKUAttribute,
  Barcode,
} from '../models/sku.interface';

export interface SKUApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  total?: number;
}

export interface SKUListResponse extends SKUApiResponse<SKUItem[]> {
  total: number;
}

export interface SKUDetailResponse extends SKUApiResponse<SKUDetail> {}

export interface SKUActionResponse extends SKUApiResponse<boolean> {
  affectedCount?: number;
}

export interface SkuSyncHistoryResponse {
  data: SyncLogItem[];
  totalRecords: number;
}

export interface SkuActivationResult {
  skuId: number;
  success: boolean;
  message: string;
}

type SkuAttributeValueWithSource = SkuAttributeValue & {
  source?: number;
};

@Injectable({
  providedIn: 'root',
})
export class SKURealApiService {
  private readonly httpClient = inject(HttpClient);
  private readonly basePath = inject(BASE_PATH);

  constructor(
    private goodsSkuController: GoodsSkuControllerService,
    private skuAuditController: SkuAuditControllerService,
  ) {}

  // Map API response to SKUItem interface
  private mapApiResponseToSKUItem(apiItem: GoodsSkuPageVO): SKUItem {
    return {
      id: apiItem.id || 0,
      serialNumber: 0, // Will be set by the component
      productCategory: apiItem.categoryName || '',
      productCode: apiItem.spuCode || '',
      productName: apiItem.spuName || '',
      skuCode: apiItem.skuCode || '',
      skuName: apiItem.skuName || '',
      materialUrl: apiItem.materialMainImages?.[0] || '',
      materialName: apiItem.materialName || '',
      costPrice: apiItem.costPrice || 0,
      sellingPrice: apiItem.salesPrice || 0,
      marketPrice: apiItem.marketPrice,
      settlementPrice: apiItem.clearingPrice,
      distributionPrice: apiItem.distributionPrice,
      internalAllocationPrice: apiItem.wholesalePrice,
      marketingPrice: apiItem.activityPrice,
      wholesalePrice: apiItem.wholesalePrice,
      productWeight: apiItem.weight,
      productHeight: apiItem.height,
      productLength: apiItem.length,
      productWidth: apiItem.width,
      productBrand: '', // Not available in API response
      mainImages: apiItem.materialMainImages || [],
      detailImages: [], // Not available in API response
      packageSkus: [], // Will be populated separately if needed
      remarks: apiItem.goodsRemark || '',
      status: this.mapBackendStatusToFrontend(apiItem.status || 0).toString(),
      showFlag: apiItem.showFlag ?? 1,
      skuType: this.mapSetFlagToSKUType(apiItem.setFlag || 0),
      auditStatus: apiItem.auditIdea || '',
      auditComments: apiItem.auditIdea || '',
      skuAttributeList: this.mapSkuAttributeList(
        apiItem.skuAttributeList || [],
      ),
      creationDate:
        (apiItem as any).creationDate || apiItem.auditCommitDate || '',
      activityPrice: apiItem.activityPrice,
      clearingPrice: apiItem.clearingPrice,
      vendorList:
        apiItem.vendorList
          ?.map((v: VendorSkuVO) => v.vendorName)
          .filter((name: string | undefined): name is string => !!name) || [],
      isPriceIncludeVAT: apiItem.isPriceIncludeVAT ?? null,
      taxId: (apiItem as any).taxId ?? null,
      taxCode: (apiItem as any).taxCode ?? null,
    };
  }

  private mapSetFlagToSKUType(setFlag: number): SKUType {
    switch (setFlag) {
      case 0:
        return 'ordinary';
      case 1:
        return 'package';
      default:
        return 'ordinary';
    }
  }

  private mapSkuAttributeList(
    apiAttributes: SkuAttributeValueWithSource[],
  ): SKUAttribute[] {
    return apiAttributes.map((attr) => ({
      id: attr.id?.toString() || '',
      skuId: attr.skuId?.toString() || '',
      attributeName: attr.attributeName || '',
      attributeValue: attr.attributeValue || '',
      attributeType: attr.attributeType || 0,
      source: attr.source ?? 0, // Use source property from API, default to 0 if not present
      isSku: attr.isSku || false,
    }));
  }

  /**
   * Map backend status codes to frontend status codes
   * Backend: 0-Draft, 1-Rejected, 2-Under Review, 3-Effective, 4-Effective (Under Revision), 5-Effective (Rejected)
   * Frontend: 0-Draft, 1-Rejected, 2-Under Review, 3-Effective, 4-Effective - Editing, 5-Effective - Rejected
   */
  private mapBackendStatusToFrontend(backendStatus: number): number {
    switch (backendStatus) {
      case 0: // Backend: Draft -> Frontend: Draft
        return 0;
      case 1: // Backend: Rejected -> Frontend: Rejected
        return 1;
      case 2: // Backend: Under Review -> Frontend: Under Review
        return 2;
      case 3: // Backend: Effective -> Frontend: Effective
        return 3;
      case 4: // Backend: Effective (Under Revision) -> Frontend: Effective - Editing
        return 4;
      case 5: // Backend: Effective (Rejected) -> Frontend: Effective - Rejected
        return 5;
      default:
        return backendStatus; // Keep as is if unknown
    }
  }

  private mapSKUTypeToSetFlag(skuType: SKUType): number {
    switch (skuType) {
      case 'ordinary':
        return 0;
      case 'package':
        return 1;
      case 'draft':
        return 0; // Draft items are also single items
      default:
        return 0;
    }
  }

  // Legacy methods for backward compatibility
  getSkuItems(): Observable<SKUListResponse> {
    return this.getSkuItemsPaginated(0, 20).pipe(
      map((response) => ({
        success: true,
        data: response.content,
        total: response.totalElements,
      })),
    );
  }

  getSkuItemsByType(type: SKUType): Observable<SKUListResponse> {
    const setFlag = this.mapSKUTypeToSetFlag(type);
    return this.goodsSkuController
      .getList3(
        undefined, // spuName
        undefined, // spuCode
        undefined, // spuType
        setFlag, // setFlag
        undefined, // categoryIdList
        undefined, // status
        undefined, // skuName
        undefined, // skuCode
        0, // page
        20, // size
        undefined, // sortBy
        undefined, // ascending
      )
      .pipe(
        map((response) => {
          const items = (response.pageContent || []).map((item) =>
            this.mapApiResponseToSKUItem(item),
          );
          return {
            success: true,
            data: items,
            total: response.totalElements || 0,
          };
        }),
      );
  }

  searchSkuItems(
    searchData: SKUFormData,
    type: SKUType,
  ): Observable<SKUListResponse> {
    const setFlag = this.mapSKUTypeToSetFlag(type);

    // Map search data to API parameters
    const statusArray = searchData.status
      ? Array.isArray(searchData.status)
        ? searchData.status.map((s) => parseInt(s, 10))
        : [parseInt(searchData.status, 10)]
      : undefined;

    return this.goodsSkuController
      .getList3(
        searchData.productName, // spuName
        searchData.productCode, // spuCode
        undefined, // spuType
        setFlag, // setFlag
        undefined, // categoryIdList - would need category ID mapping
        statusArray, // status
        searchData.skuName, // skuName
        searchData.skuCode, // skuCode
        0, // page
        20, // size
        undefined, // sortBy
        undefined, // ascending
      )
      .pipe(
        map((response) => {
          const items = (response.pageContent || []).map((item) =>
            this.mapApiResponseToSKUItem(item),
          );
          return {
            success: true,
            data: items,
            total: response.totalElements || 0,
          };
        }),
      );
  }

  getSkuItemById(id: number): Observable<SKUDetailResponse> {
    return this.goodsSkuController.detail1(id, false).pipe(
      map((response) => ({
        success: true,
        data: response as any, // Will need proper mapping
      })),
    );
  }

  private hasComponentSkuIds(
    skuDetailList: SKUDetail['skuDetailList'],
  ): boolean {
    return Boolean(
      skuDetailList?.length &&
        skuDetailList.every(
          (component) => {
            const packageSkuId = component.packageSkuId;
            return (
              typeof packageSkuId === 'number' &&
              Number.isInteger(packageSkuId) &&
              packageSkuId > 0
            );
          },
        ),
    );
  }

  getSkuDetailById(id: string): Observable<SKUDetailResponse> {
    return this.goodsSkuController.detail1(parseInt(id, 10), false).pipe(
      switchMap((response: any) => {
        // Map barcodes from API response if available
        // API returns barcodes as array of objects with: id, barcodeValue, barcodeType, skuId, etc.
        const mappedBarcodes =
          response.barcodes && Array.isArray(response.barcodes)
            ? response.barcodes.map((b: any) => ({
                id: b.id,
                barcodeValue: b.barcodeValue || '',
                barcodeType: (b.barcodeType || 'EAN') as Barcode['barcodeType'],
              }))
            : undefined;

        // Normalize image fields: API may return various names (materialMainImages, imageList, mainImage, detailImage, images, snake_case)
        const toArray = (v: any): string[] => {
          if (v == null) return [];
          if (Array.isArray(v))
            return v.filter((u: any) => typeof u === 'string' && u !== '');
          if (typeof v === 'string' && v !== '') return [v];
          return [];
        };
        let materialMainImages = toArray(
          response.materialMainImages ?? response.material_main_images,
        );
        if (materialMainImages.length === 0)
          materialMainImages = toArray(
            response.mainImage ?? response.main_image,
          );
        let imageList = toArray(response.imageList ?? response.image_list);
        if (imageList.length === 0)
          imageList = toArray(
            response.detailImage ?? response.detail_image ?? response.images,
          );

        const mappedData: SKUDetail = {
          ...response,
          materialMainImages,
          imageList,
          barcodes: mappedBarcodes,
        };

        if (
          mappedData.setFlag !== 1 ||
          !mappedData.skuCode ||
          this.hasComponentSkuIds(mappedData.skuDetailList)
        ) {
          return of({
            success: true,
            data: mappedData,
          });
        }

        return this.goodsSkuController
          .getMealsGoodsList(
            [mappedData.skuCode],
            'body',
            false,
            { httpHeaderAccept: 'application/json' },
          )
          .pipe(
            map((components) => ({
              success: true,
              data: {
                ...mappedData,
                skuDetailList: components.map((component) => ({
                  packageSkuId: component.skuId,
                  skuId: component.skuId,
                  skuCode: component.skuCode,
                  skuName: component.skuName,
                  spuCode: component.spuCode,
                  spuName: component.spuName,
                  costPrice: component.costPrice,
                  salesPrice: component.salesPrice,
                  num: component.num,
                })),
              },
            })),
            catchError(() =>
              of({
                success: true,
                data: mappedData,
              }),
            ),
          );
      }),
      map((response) => {
        return {
          success: true,
          data: response.data,
        };
      }),
    );
  }

  deleteSkuItems(ids: number[]): Observable<SKUActionResponse> {
    return this.goodsSkuController.deleteByIds1({ ids }).pipe(
      map(() => ({
        success: true,
        data: true,
        affectedCount: ids.length,
      })),
    );
  }

  submitSkuItems(ids: number[]): Observable<SKUActionResponse> {
    return this.goodsSkuController.batchAudit({ skuIds: ids }).pipe(
      map(() => ({
        success: true,
        data: true,
        affectedCount: ids.length,
      })),
    );
  }

  // Independent Activate/Deactivate — flips showFlag only, never the
  // approval status. Calls the endpoint directly via HttpClient (like
  // ClassifyService.reorderCategories) because it isn't in the generated SDK
  // yet; swap to the generated GoodsSkuControllerService method once
  // `npm run api:generate` is re-run after this backend endpoint ships.
  toggleSkuActivation(
    skuIds: number[],
    active: boolean,
    reason?: string,
  ): Observable<SkuActivationResult[]> {
    return this.httpClient.patch<SkuActivationResult[]>(
      `${environment.goodsServiceBasePath}/v1/goods/sku/activation`,
      { skuIds, active, reason },
    );
  }

  auditSkuItem(
    id: number,
    approve: boolean,
    comment?: string,
  ): Observable<SKUActionResponse> {
    return this.skuAuditController
      .audit({ id, flag: approve, auditIdea: comment })
      .pipe(
        map(() => ({
          success: true,
          data: true,
          affectedCount: 1,
        })),
      );
  }

  addSkuItem(type: SKUType): Observable<SKUActionResponse> {
    // This would need to be implemented based on the actual API
    return new Observable((observer) => {
      observer.next({
        success: true,
        data: true,
      });
      observer.complete();
    });
  }

  updateSkuItem(
    id: string,
    skuData: Partial<SKUDetail>,
  ): Observable<SKUActionResponse> {
    const skuId = parseInt(id, 10);

    // Map the form data to the API DTO structure
    const skuUpdateInfo: any = {
      id: skuId,
      skuCode: skuData.skuCode,
      skuName: skuData.skuName,
      costPrice: skuData.costPrice,
      salesPrice: skuData.salesPrice,
      activityPrice: skuData.activityPrice,
      marketPrice: skuData.marketPrice,
      distributionPrice: skuData.distributionPrice,
      wholesalePrice: skuData.wholesalePrice,
      clearingPrice: skuData.clearingPrice,
      weight: skuData.weight,
      length: skuData.length,
      width: skuData.width,
      height: skuData.height,
      // showFlag is intentionally omitted — Activate/Deactivate is
      // independent of this generic update flow; the backend now ignores any
      // showFlag sent here and always preserves the SKU's live value.
      showFlag: 1,
      taxId: skuData.taxId ?? null,
      isPriceIncludeVAT: skuData.isPriceIncludeVAT ?? null,
    };

    if (skuData.setFlag === 1 && Array.isArray(skuData.skuDetailList)) {
      skuUpdateInfo.skuDetailList = skuData.skuDetailList.map((component) => ({
        packageSkuId: component.packageSkuId,
        num: component.num,
      }));
    }

    // Handle SKU attributes if provided
    if (skuData.skuAttributeList && Array.isArray(skuData.skuAttributeList)) {
      const skuAttributeList: SkuAttributeValueWithSource[] =
        skuData.skuAttributeList.map((attr) => ({
          id: parseInt(attr.id) || undefined,
          skuId: skuId,
          attributeName: attr.attributeName,
          attributeValue: attr.attributeValue,
          attributeType: attr.attributeType,
          source: attr.source ?? 0, // Use source property from API, default to 0 if not present
          isSku: attr.isSku || false,
        }));

      skuUpdateInfo.skuAttributeList = skuAttributeList;
    }

    // Handle other attributes if provided
    if (
      skuData.otherAttributeList &&
      Array.isArray(skuData.otherAttributeList)
    ) {
      const otherAttributeList: SkuAttributeValueWithSource[] =
        skuData.otherAttributeList.map((attr) => ({
          id: parseInt(attr.id) || undefined,
          skuId: skuId,
          attributeName: attr.attributeName,
          attributeValue: attr.attributeValue,
          attributeType: attr.attributeType,
          source: attr.source ?? 0, // Use source property from API, default to 0 if not present
          isSku: attr.isSku || false,
        }));

      skuUpdateInfo.otherAttributeList = otherAttributeList;
    }

    // Handle barcodes - always send array (even if empty) to allow clearing all barcodes
    if (skuData.barcodes !== undefined && Array.isArray(skuData.barcodes)) {
      const barcodes: BarcodeRequestDTO[] = skuData.barcodes.map(
        (barcode: Barcode) => ({
          barcodeValue: barcode.barcodeValue,
          barcodeType: barcode.barcodeType as BarcodeRequestDTO.BarcodeTypeEnum,
        }),
      );
      skuUpdateInfo.barcodes = barcodes;
    }

    const updatePayload: SkuUpdateDTO = {
      sku: skuUpdateInfo,
      flag: false, // false = submit, true = save as draft
    };

    return this.goodsSkuController
      .updateOne(skuId, updatePayload as any, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map(() => {
          return {
            success: true,
            data: true,
          };
        }),
      );
  }

  // New methods for backend integration
  searchSkuItemsPaginated(
    searchPayload: SKUSearchPayload,
  ): Observable<SKUPaginatedResponse> {
    const setFlag =
      searchPayload.setFlag === null
        ? undefined
        : (searchPayload.setFlag ??
          this.mapSKUTypeToSetFlag(searchPayload.type || 'ordinary'));

    let statusArray: number[] | undefined;
    if (searchPayload.status == null) {
      statusArray = undefined;
    } else if (Array.isArray(searchPayload.status)) {
      statusArray =
        searchPayload.status.length > 0 ? searchPayload.status : undefined;
    } else {
      statusArray = [searchPayload.status];
    }

    // Convert categoryIdList from string array to number array
    const categoryIdArray =
      searchPayload.categoryIdList && searchPayload.categoryIdList.length > 0
        ? searchPayload.categoryIdList.map((id) => parseInt(id.toString(), 10))
        : undefined;

    return this.goodsSkuController
      .getList3(
        searchPayload.spuName || undefined, // spuName
        searchPayload.spuCode || undefined, // spuCode
        undefined, // spuType
        setFlag, // setFlag
        categoryIdArray, // categoryIdList
        statusArray, // status
        searchPayload.skuName || undefined, // skuName
        searchPayload.skuCode || undefined, // skuCode
        searchPayload.page || 0, // page
        searchPayload.size || 20, // size
        searchPayload.sortBy || undefined, // sortBy
        searchPayload.ascending, // ascending
      )
      .pipe(
        map((response) => {
          const items = (response.pageContent || []).map((item, index) => ({
            ...this.mapApiResponseToSKUItem(item),
            serialNumber:
              (response.number || 0) * (response.size || 20) + index + 1,
          }));

          return {
            content: items,
            totalElements: response.totalElements || 0,
            numberOfElements: response.numberOfElements || 0,
            size: response.size || 20,
            number: response.number || 0,
            totalPages: response.totalPages || 0,
            empty: response.empty || false,
          };
        }),
      );
  }

  getSkuItemsPaginated(
    page: number,
    size: number,
  ): Observable<SKUPaginatedResponse> {
    return this.goodsSkuController
      .getList3(
        undefined, // spuName
        undefined, // spuCode
        undefined, // spuType
        0, // setFlag - default to ordinary items
        undefined, // categoryIdList
        undefined, // status
        undefined, // skuName
        undefined, // skuCode
        page, // page
        size, // size
        undefined, // sortBy
        undefined, // ascending
      )
      .pipe(
        map((response) => {
          const items = (response.pageContent || []).map((item, index) => ({
            ...this.mapApiResponseToSKUItem(item),
            serialNumber:
              (response.number || 0) * (response.size || 20) + index + 1,
          }));

          return {
            content: items,
            totalElements: response.totalElements || 0,
            numberOfElements: response.numberOfElements || 0,
            size: response.size || 20,
            number: response.number || 0,
            totalPages: response.totalPages || 0,
            empty: response.empty || false,
          };
        }),
      );
  }

  /**
   * RBA-2268: GET /v1/goods/sku/{id}/sync-history. Called directly via HttpClient rather than
   * GoodsSkuControllerService: that generated client is only regenerated (`npm run api:generate
   * --service goods-service`) against develop's deployed spec, and this endpoint isn't there yet.
   * Once it merges, regenerate the client and switch this method to call it instead.
   */
  getSkuSyncHistory(
    skuId: number,
    page: number = 0,
    size: number = 20,
  ): Observable<SkuSyncHistoryResponse> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size));

    return this.httpClient
      .get<any>(`${this.basePath}/v1/goods/sku/${skuId}/sync-history`, {
        params,
      })
      .pipe(
        map((response) => ({
          data: (response?.pageContent ?? []) as SyncLogItem[],
          totalRecords: response?.totalElements ?? 0,
        })),
        catchError((error) => {
          console.error('Error fetching SKU sync history:', error);
          throw error;
        }),
      );
  }
}
